import { useState, useRef } from "react";
import Container from "Components/Container/Container";
import backgroud from "assests/bghomd.png";
import { useTranslation } from "react-i18next";
import { Play, Pause, Timer, ArrowRight } from "@phosphor-icons/react";
import BASE_URL from "Utilities/BASE_URL";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Pagination, Autoplay } from "swiper/modules";
import useButtonConfig from "Hooks/useButtonConfig";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";

const getCourseThumbnail = (course) => {
  if (course?.trailer?.assets?.thumbnail) return course.trailer.assets.thumbnail;
  if (course?.thumbnail) return course.thumbnail;
  if (course?.image) return `${BASE_URL.replace(/\/api$/, "")}/${course.image}`;
  return null;
};

const Hero = ({ courses, hero }) => {
  const { t, i18n } = useTranslation();
  const [isPaused, setIsPaused] = useState(false);
  const swiperRef = useRef(null);
  const buttonConfig = useButtonConfig();
  const isRTL = i18n.language === "ar";

  const column1 = courses?.filter((_, index) => index % 2 === 0) || [];
  const column2 = courses?.filter((_, index) => index % 2 !== 0) || [];
  const column1Images = [...column1, ...column1, ...column1];
  const column2Images = [...column2, ...column2, ...column2];

  const handlePauseToggle = () => {
    setIsPaused(!isPaused);
    if (swiperRef.current?.swiper) {
      if (!isPaused) {
        swiperRef.current.swiper.autoplay.stop();
      } else {
        swiperRef.current.swiper.autoplay.start();
      }
    }
  };

  return (
    <div
      className="relative min-h-0 lg:h-screen overflow-hidden bg-black"
      style={{
        backgroundImage: `url(${backgroud})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Desktop Layout */}
      <div className="hidden lg:block h-full">
        <Container className="h-full">
          <div className="grid grid-cols-2 gap-8 h-screen items-center relative z-10">
            {/* Left side — Content */}
            <div className="flex flex-col justify-center items-start px-4 lg:px-8">
              {/* Main headline from dashboard */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
                {hero?.title}
              </h1>

              {/* Subtitle from dashboard */}
              <p className="text-lg text-gray-300 mt-4 max-w-md whitespace-pre-line">
                {hero?.text}
              </p>

              {/* What is DeeClass — one-liner */}
              <div className="flex items-center gap-2 mt-6 text-gray-400 text-sm">
                <div className="w-8 h-px bg-primary"></div>
                <span>{t("hero.tagline")}</span>
              </div>

              {/* CTA Button with time messaging */}
              <button
                onClick={buttonConfig.action}
                className="mt-8 group flex items-center gap-3 px-8 py-4 bg-primary hover:bg-darkPrimary text-white font-semibold rounded-xl transition-all duration-300"
              >
                <span className="text-base">{buttonConfig.text}</span>
                <ArrowRight
                  size={20}
                  className={`transition-transform group-hover:translate-x-1 ${isRTL ? "rotate-180 group-hover:-translate-x-1" : ""}`}
                />
              </button>

              {/* Subtitle under CTA */}
              {buttonConfig.subtitle && (
                <p className="text-xs text-gray-500 mt-3 flex items-center gap-1.5">
                  <Timer size={14} />
                  {buttonConfig.subtitle}
                </p>
              )}

              {/* How it works — 3 steps */}
              <div className="flex items-center gap-6 mt-10">
                {[
                  { num: "1", label: t("hero.step_1") },
                  { num: "2", label: t("hero.step_2") },
                  { num: "3", label: t("hero.step_3") },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center">
                      {step.num}
                    </span>
                    <span className="text-xs text-gray-400">{step.label}</span>
                    {i < 2 && (
                      <div className="w-6 h-px bg-gray-600 ms-2"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right side — Infinite scrolling images */}
            <div className="relative flex gap-4 h-screen overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-black via-black/80 to-transparent z-10 pointer-events-none" />
              {/* Column 1 */}
              <div className="flex-1 overflow-hidden">
                <div className={`flex flex-col gap-4 ${isPaused ? "" : "animate-scroll-up"}`}>
                  {column1Images.map((course, index) => (
                    <div
                      key={`col1-${course.id}-${index}`}
                      className="relative rounded-xl overflow-hidden aspect-[3/4] flex-shrink-0"
                    >
                      <img
                        src={getCourseThumbnail(course)}
                        alt={course.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-white font-semibold text-sm line-clamp-2">
                          {course.name}
                        </h3>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column 2 */}
              <div className="flex-1 overflow-hidden mt-8">
                <div className={`flex flex-col gap-4 ${isPaused ? "" : "animate-scroll-up-slow"}`}>
                  {column2Images.map((course, index) => (
                    <div
                      key={`col2-${course.id}-${index}`}
                      className="relative rounded-xl overflow-hidden aspect-[3/4] flex-shrink-0"
                    >
                      <img
                        src={getCourseThumbnail(course)}
                        alt={course.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-white font-semibold text-sm line-clamp-2">
                          {course.name}
                        </h3>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden flex flex-col justify-center relative z-10 pt-20 pb-12 sm:pt-24">
        <Container>
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
              {hero?.title || t("home.title")}
            </h1>
            <p className="text-base text-gray-300 mt-4 max-w-md mx-auto whitespace-pre-line">
              {hero?.text}
            </p>
            {/* Tagline */}
            <p className="text-sm text-gray-400 mt-4">
              {t("hero.tagline")}
            </p>
          </div>

          {/* Course Slider */}
          <div className="relative mobile-hero-slider">
            <Swiper
              ref={swiperRef}
              effect={"coverflow"}
              grabCursor={true}
              centeredSlides={true}
              loop={courses?.length > 2}
              slidesPerView={"auto"}
              speed={1200}
              watchSlidesProgress={true}
              coverflowEffect={{
                rotate: 0,
                stretch: 0,
                depth: 100,
                modifier: 2.5,
                slideShadows: false,
              }}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              autoplay={{
                delay: 6000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              modules={[EffectCoverflow, Pagination, Autoplay]}
              className="hero-mobile-slider"
            >
              {courses?.map((course, index) => (
                <SwiperSlide key={`mobile-${course.id}-${index}`} className="hero-mobile-slide">
                  <div className="relative overflow-hidden rounded-xl aspect-[3/4]">
                    <img
                      src={getCourseThumbnail(course)}
                      alt={course.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white font-semibold text-base line-clamp-2">
                        {course.name}
                      </h3>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Action card (steps + CTA) lives as its own home-page section now — see HeroActionCard */}
        </Container>
      </div>

      {/* Pause/Play — desktop only (mobile uses its own Swiper without the scroll columns) */}
      <button
        onClick={handlePauseToggle}
        className="hidden lg:flex absolute lg:bottom-8 lg:right-8 z-20 p-3 bg-white/20 hover:bg-white/30 rounded-full transition-colors duration-200 text-white min-h-[44px] min-w-[44px] items-center justify-center"
        aria-label={isPaused ? "Play" : "Pause"}
      >
        {isPaused ? <Play size={24} weight="fill" /> : <Pause size={24} weight="fill" />}
      </button>
    </div>
  );
};

export default Hero;
